import UserIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { getBusinessGlobalContacts } from 'lib/api/contacts';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import EnhancedTable from 'lib/designSystem/Table';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { Contact } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import AddConfirmationDialog from './AddConfirmationDialog';

const EmptyNetwork = () => {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" py={3}>
      <Stack spacing={1} alignItems="center">
        <UserIcon sx={{ color: 'text.disabled', fontSize: 40 }} />
        <Typography variant="body2" color="text.secondary">
          La empresa no tiene contactos en la red.
        </Typography>
      </Stack>
    </Box>
  );
};

const ContactNetwork = ({
  open,
  setOpen,
  identifier,
  onContactAdded,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  identifier: string;
  onContactAdded?: () => Promise<void>;
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState<Contact[] | undefined>();
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const fetchContacts = async () => {
    try {
      if (!identifier) {
        showAlert(
          dispatch,
          'error',
          'No se ha encontrado el identificador de la empresa',
        );
        return;
      }
      setLoading(true);
      const contacts = await getBusinessGlobalContacts(dispatch, identifier);
      setContacts(contacts);
    } catch (error) {
      errorHandler(dispatch, error, 'Error al obtener los contactos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      void fetchContacts();
    }
  }, [identifier, open]);

  const handleContactSelect = (contactId: number) => {
    const contact = contacts?.find((contact) => contact.id === contactId);
    if (!contact) return;
    setSelectedContact(contact);
    setConfirmationOpen(true);
  };

  const handleConfirmationSuccess = () => {
    setConfirmationOpen(false);
    setSelectedContact(null);
    if (onContactAdded) void onContactAdded();
    setOpen(false);
  };

  const dataHeaders = [
    { key: 'name', label: 'Nombre' },
    { key: 'email', label: 'Email', type: 'copyable' },
    { key: 'phone', label: 'Teléfono' },
    { key: 'role', label: 'Rol' },
    { key: 'customRole', label: 'Rol personalizado' },
  ];

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          onClose={() => setOpen(false)}
          title="Buscar contacto en red"
          icon={<SearchIcon color="primary" />}
          color="primary"
        />
        <DialogContent>
          {contacts && contacts.length > 0 ? (
            <EnhancedTable
              data={contacts || []}
              dataHeaders={dataHeaders}
              handleClickRow={(contactId: number) =>
                handleContactSelect(contactId)
              }
            />
          ) : (
            <EmptyNetwork />
          )}
        </DialogContent>
      </Dialog>

      {selectedContact && (
        <AddConfirmationDialog
          open={confirmationOpen}
          setOpen={setConfirmationOpen}
          contact={selectedContact}
          businessId={identifier}
          onSuccess={handleConfirmationSuccess}
        />
      )}
    </>
  );
};

export default ContactNetwork;
